import UUID from "../../Generic/UUID";
import { getOption, setOption } from "../Options/Options";

interface SerialPortOptions<Output> {
    port: SerialOptions;
    id?: string;
    transformReader?: (stream: ReadableStream<BufferSource>) => {
        reader: ReadableStreamDefaultReader<Output>;
        closePromise: Promise<void>;
    };
}

class SerialPortNotSupportedError extends Error {}
class SerialPortResponseTimedOut extends Error {}

type DataCallback<T> = (data: T) => void;

export class SerialPortHandler<Output = Uint8Array> {
    protected port?: SerialPort;
    protected reader?: ReadableStreamDefaultReader<Output>;
    protected writer?: WritableStreamDefaultWriter<Uint8Array>;
    protected isOpen: boolean;
    protected readableStreamClosed?: Promise<void>;
    protected callbacks: Set<DataCallback<Output>>;

    constructor(protected options: SerialPortOptions<Output>) {
        if(!navigator.serial) {
            throw new SerialPortNotSupportedError();
        }

        this.isOpen = false;
        this.callbacks = new Set();
    }

    protected fireReceivedData(data: Output): void {
        for(const callback of this.callbacks) {
            callback(data);
        }
    }

    protected waitForData(callback: DataCallback<Output>): void {
        this.callbacks.add(callback);
    }

    protected stopWaitingForData(callback: DataCallback<Output>): void {
        this.callbacks.delete(callback);
    }

    protected async read(): Promise<void> {
        if(!this.port) {
            throw new TypeError("The serial port hasn't been initialised yet");
        }

        if(!this.isOpen) {
            throw new TypeError("The serial port isn't open yet");
        }

        if(this.options.transformReader) {
            const { reader, closePromise } = this.options.transformReader(this.port.readable);

            this.reader = reader;
            this.readableStreamClosed = closePromise;
        } else {
            this.reader = this.port.readable.getReader() as unknown as ReadableStreamDefaultReader<Output>;
        }

        while(true) {
            const { value, done } = await this.reader.read();

            if(done) {
                this.reader.releaseLock();
                break;
            }

            if(value) {
                this.fireReceivedData(value);
            }
        }
    }

    protected async write(data: Uint8Array): Promise<void> {
        if(!this.isOpen) {
            throw new TypeError("The serial port isn't open yet");
        }

        if(!this.writer) {
            if(!this.port) {
                throw new TypeError("The serial port hasn't been initialised yet");
            }

            this.writer = this.port.writable.getWriter();
        }

        await this.writer.write(data);
    }

    protected convertDataFromASCIIToDec(data: string): Uint8Array {
        // This will take any ASCII string and convert it to decimal to send to the serial port
        // this expects a well formed string and won't work with UTF-8 data.
        return new Uint8Array(
            data.split("")
                .map(char => `0${char.charCodeAt(0).toString(16)}`.slice(-2))
                .join("")
                .match(/.{1,2}/g)
                ?.map(byte => parseInt(byte, 16)) || []
        );
    }

    public send(data: string, isExpectedResponse: (data: Output) => boolean, timeout: number): Promise<Output>;
    public send(data: string): Promise<void>;
    public send(
        data: string,
        isExpectedResponse?: (data: Output) => boolean,
        timeout?: number
    ): Promise<void | Output> {
        return new Promise((res, rej) => {
            let timer: ReturnType<typeof setTimeout> | undefined;

            if(isExpectedResponse) {
                if(!timeout) {
                    throw new TypeError("Timeout is required when expecting a response");
                }

                const callback = (data: Output): void => {
                    if(isExpectedResponse(data)) {
                        this.stopWaitingForData(callback);

                        res(data);

                        if(timer) {
                            clearTimeout(timer);
                        }
                    }
                };

                this.waitForData(callback);
                timer = setTimeout(() => {
                    this.stopWaitingForData(callback);
                    rej(new SerialPortResponseTimedOut());
                }, timeout);
            }

            data = data.replace("\\r", "\r");

            const writePromise = this.write(this.convertDataFromASCIIToDec(data));

            if(!isExpectedResponse) {
                writePromise.then(res);
            }
        });
    }

    public async close(): Promise<void> {
        if(!this.isOpen || !this.port) {
            return;
        }

        this.isOpen = false;

        if(this.reader) {
            if(!this.readableStreamClosed) {
                await this.reader.cancel();
            } else {
                // noinspection ES6MissingAwait
                this.reader.cancel();
                await this.readableStreamClosed.catch(() => { /* no-op */ });
            }
        }

        if(this.writer) {
            await this.writer.close();
        }

        await this.port.close();
    }

    public getPortId(): string {
        return this.options.id || "";
    }

    protected async getPort(): Promise<SerialPort> {
        if(this.port) {
            return this.port;
        }

        let previouslyAvailablePorts = getOption("__serial_ports");
        const currentlyAvailablePorts = await navigator.serial.getPorts();

        if(currentlyAvailablePorts.length !== previouslyAvailablePorts.length) {
            // Throw out the previously available ports
            setOption("__serial_ports", []);
            previouslyAvailablePorts = [];
        }

        if(this.options.id) {
            // Attempt to find the port using our ID
            // This will be made significantly easier once
            // https://github.com/WICG/serial/issues/128
            // has been completed.
            // Currently we're presuming ports are always returned
            // in the same order, but there is no guarantee that this
            // is the case.
            for(let i = 0, l = previouslyAvailablePorts.length; i < l; i++) {
                if(previouslyAvailablePorts[i] === this.options.id) {
                    return currentlyAvailablePorts[i];
                }
            }
        }

        const port = await navigator.serial.requestPort();

        const id = UUID.generate();

        previouslyAvailablePorts.push(id);
        setOption("__serial_ports", previouslyAvailablePorts);
        this.options.id = id;

        return port;
    }

    public async open(): Promise<void> {
        if(this.isOpen) {
            return;
        }

        if(!this.port) {
            this.port = await this.getPort();
        }

        await this.port.open(this.options.port);

        this.isOpen = true;

        // noinspection ES6MissingAwait
        this.read();
    }
}
